import type { AuthChangeEvent, Session, SupabaseClient } from "@supabase/supabase-js";

/** The slice of Supabase auth this store uses; narrow so tests can fake it. */
export type AuthLike = Pick<
  SupabaseClient["auth"],
  "getSession" | "onAuthStateChange" | "signInWithOtp" | "verifyOtp" | "signInWithOAuth" | "signOut"
>;

export type SessionStatus = "loading" | "signed_out" | "signed_in";

export interface SessionSnapshot {
  readonly status: SessionStatus;
  readonly userId: string | null;
  readonly email: string | null;
}

const LOADING: SessionSnapshot = Object.freeze({ status: "loading", userId: null, email: null });
const SIGNED_OUT: SessionSnapshot = Object.freeze({ status: "signed_out", userId: null, email: null });

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CODE_LENGTH = 6;

/**
 * Where the signed-in state lives.
 *
 * Wraps Supabase auth in a tiny external store: an immutable snapshot for
 * React (`useSyncExternalStore`), and `getToken()` for the API client, which
 * must read the *current* access token on every call because Supabase
 * refreshes it underneath us. Nothing else in the app talks to Supabase auth
 * directly.
 */
export interface SessionStore {
  getSnapshot(): SessionSnapshot;
  subscribe(listener: () => void): () => void;
  /** Resolves once the persisted session (if any) has been read. */
  ready(): Promise<void>;
  /** The current bearer token, or null when signed out. */
  getToken(): string | null;
  sendEmailCode(email: string): Promise<void>;
  verifyEmailCode(email: string, code: string): Promise<void>;
  signInWithGoogle(redirectTo: string): Promise<void>;
  signOut(): Promise<void>;
  dispose(): void;
}

export function createSessionStore(auth: AuthLike): SessionStore {
  let snapshot: SessionSnapshot = LOADING;
  let session: Session | null = null;
  const listeners = new Set<() => void>();

  function publish(next: Session | null): void {
    session = next;
    snapshot = toSnapshot(next);
    for (const listener of listeners) listener();
  }

  const readyPromise = auth.getSession().then(({ data, error }) => {
    if (error) {
      // A storage read that failed is a signed-out person, not a stuck spinner.
      publish(null);
      return;
    }
    publish(data.session);
  });

  const {
    data: { subscription },
  } = auth.onAuthStateChange((_event: AuthChangeEvent, next: Session | null) => {
    publish(next);
  });

  return {
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    ready: () => readyPromise,
    getToken: () => session?.access_token ?? null,

    async sendEmailCode(rawEmail) {
      const email = normaliseEmail(rawEmail);
      const { error } = await auth.signInWithOtp({ email, options: { shouldCreateUser: true } });
      if (error) throw new AuthFailure(error.message);
    },

    async verifyEmailCode(rawEmail, rawCode) {
      const email = normaliseEmail(rawEmail);
      const token = rawCode.replace(/\D/g, "");
      if (token.length !== CODE_LENGTH) {
        throw new AuthFailure(`Enter the ${CODE_LENGTH}-digit code from your email.`);
      }
      const { error } = await auth.verifyOtp({ email, token, type: "email" });
      if (error) throw new AuthFailure(error.message);
    },

    async signInWithGoogle(redirectTo) {
      const { error } = await auth.signInWithOAuth({ provider: "google", options: { redirectTo } });
      if (error) throw new AuthFailure(error.message);
    },

    async signOut() {
      const { error } = await auth.signOut();
      if (error) throw new AuthFailure(error.message);
      publish(null);
    },

    dispose() {
      subscription.unsubscribe();
      listeners.clear();
    },
  };
}

/** A sign-in step that did not go through, with a message safe to show. */
export class AuthFailure extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AuthFailure";
  }
}

function normaliseEmail(raw: string): string {
  const email = raw.trim().toLowerCase();
  if (!EMAIL_PATTERN.test(email)) throw new AuthFailure("Enter a valid email address.");
  return email;
}

function toSnapshot(next: Session | null): SessionSnapshot {
  if (!next) return SIGNED_OUT;
  return Object.freeze({
    status: "signed_in",
    userId: next.user.id,
    email: next.user.email ?? null,
  });
}
